import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import { Stack } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { CustomColumnField, MeasureField, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectTabularFields } from "../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks";
import { isCustomColumn, isDimensionBased, isMeasure } from "../../../../utilities/fieldsHelper.ts";
import { supportsFormatting } from "../../common/fields/custom-field/utilities/calculationTypeCapabilities.ts";

import DimensionFormattingContainer from "../../common/fields/format/DimensionFormattingContainer.tsx";
import { canApplyFormat } from "../../common/fields/format/dimensionFormattingHelper.tsx";
import MeasureFieldsContainer from "../../common/fields/format/MeasureFieldsContainer.tsx";
import TextFieldContainer from "./TextFieldsContainer.tsx";

const isFormattableMeasureOrCustomColumn = (f: ReportField): f is MeasureField | CustomColumnField => {
  return isMeasure(f) || (isCustomColumn(f) && supportsFormatting(f.config.calculationType));
};

const FormatContainer = ({ readonlyMode }: { readonlyMode: boolean }) => {
  const dispatch = useAppDispatch();
  const fieldsAreaValues = useSelector(selectTabularFields);

  const supportingFormattingMeasures = useMemo(
    () => fieldsAreaValues.filter(isFormattableMeasureOrCustomColumn),
    [fieldsAreaValues]
  );
  const dimensions = useMemo(() => fieldsAreaValues.filter(isDimensionBased), [fieldsAreaValues]);
  const supportingFormattingDimensions = useMemo(() => dimensions.filter(canApplyFormat), [dimensions]);

  const handleMeasureChange = useCallback(
    (measure: ReportField, changes: Partial<FieldConfiguration>) => {
      const field = fieldsAreaValues.find((f) => f.config.guid === measure.config.guid);
      if (isMeasure(field) || isCustomColumn(field)) {
        dispatch(fieldsStateActions.updateFieldConfig({ field, changes }));
      }
    },
    [dispatch, fieldsAreaValues]
  );

  const handleDimensionChange = useCallback(
    (dimension: ReportField, changes: Partial<FieldConfiguration>) => {
      const field = fieldsAreaValues.find((f) => f.config.guid === dimension.config.guid);
      if (isDimensionBased(field)) {
        dispatch(fieldsStateActions.updateFieldConfig({ field, changes }));
      }
    },
    [dispatch, fieldsAreaValues]
  );

  return (
    <ScrollableFlexContainer scrollContainerSx={{ px: "1.3rem" }}>
      <Stack sx={{ gap: 1 }}>
        <TextFieldContainer fields={dimensions} onUpdateConfig={handleDimensionChange} readonlyMode={readonlyMode} />
        <DimensionFormattingContainer
          dimensions={supportingFormattingDimensions}
          onUpdateConfig={handleDimensionChange}
          readonlyMode={readonlyMode}
        />
        <MeasureFieldsContainer
          measures={supportingFormattingMeasures}
          onChange={handleMeasureChange}
          readonlyMode={readonlyMode}
        />
      </Stack>
    </ScrollableFlexContainer>
  );
};

export default FormatContainer;
